import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Timer, MessageSquare, Network, Users, Layers, BrainCircuit, Palette, Megaphone, Gift, BookOpen, Video } from 'lucide-react';
import { MaskedLines, FadeUp, Overline, EASE } from '@/components/Reveal';
import Marquee from '@/components/Marquee';
import { SEMINARS, MARQUEE_ITEMS } from '@/data/content';

const ICONS = { Timer, MessageSquare, Network, Users, Layers, BrainCircuit, Palette, Megaphone };

const CATEGORIES = ['Todos', ...new Set(SEMINARS.map((s) => s.category))];

const BONUSES = [
  { icon: Video, title: 'Sesiones en vivo', desc: 'Una sesión semanal en vivo para retroalimentación directa con mentores del programa.' },
  { icon: Gift, title: '2 Bonos asincrónicos', desc: 'Diseño visual con herramientas creativas y storytelling con personal branding, a su propio ritmo.' },
  { icon: BookOpen, title: 'Workbooks de trabajo', desc: 'Cuadernos de ejercicios prácticos para aplicar cada seminario en su vida académica real.' },
];

export default function Seminarios() {
  const [filter, setFilter] = useState('Todos');
  const visible = filter === 'Todos' ? SEMINARS : SEMINARS.filter((s) => s.category === filter);

  return (
    <main>
      {/* ============ PAGE HERO ============ */}
      <section data-testid="seminars-hero" className="noise relative overflow-hidden bg-navy pb-20 pt-32 lg:pb-28 lg:pt-44">
        <span aria-hidden className="text-stroke-white pointer-events-none absolute -right-8 top-16 select-none font-display text-[26vw] font-extrabold leading-none">
          08
        </span>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}>
            <Overline className="text-gold">Programa Advanced</Overline>
          </motion.div>
          <MaskedLines
            as="h1"
            delay={0.2}
            className="mt-7 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
            lines={[
              <>8 seminarios</>,
              <><span className="text-crimson">tácticos</span> de alto</>,
              <>rendimiento</>,
            ]}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg"
          >
            Cada seminario instala una competencia concreta: objetivo de aprendizaje claro y un
            beneficio tangible que separa a los líderes del resto.
          </motion.p>
        </div>
      </section>

      <div className="bg-crimson text-white">
        <Marquee items={MARQUEE_ITEMS} fast testId="seminars-marquee" />
      </div>

      {/* ============ CHAPTERS ============ */}
      <section data-testid="seminars-chapters" className="bg-porcelain py-16 text-navy sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <div className="flex flex-wrap gap-3" data-testid="seminar-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`filter-chip-${c.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setFilter(c)}
                  className={`rounded-full border px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
                    filter === c
                      ? 'border-crimson bg-crimson text-white'
                      : 'border-navy/20 bg-white text-navy/60 hover:border-crimson/50 hover:text-crimson'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </FadeUp>

          <div className="mt-10">
            <AnimatePresence mode="popLayout">
              {visible.map((s, i) => {
                const Icon = ICONS[s.icon] || Timer;
                return (
                  <motion.article
                    key={s.id}
                    layout
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.6, ease: EASE, delay: i * 0.04 }}
                    data-testid={`seminar-chapter-${s.id}`}
                    className="group relative border-t border-navy/10 py-12 last:border-b lg:py-14"
                  >
                    <div className="grid gap-8 lg:grid-cols-12">
                      <div className="flex items-start gap-5 lg:col-span-3">
                        <span className="text-stroke-navy select-none font-display text-6xl font-extrabold leading-none transition-all duration-500 group-hover:[-webkit-text-stroke-color:rgba(217,27,92,0.6)] lg:text-8xl">
                          {s.id}
                        </span>
                        <span className="mt-2 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-crimson/30 text-crimson transition-colors duration-500 group-hover:bg-crimson group-hover:text-white">
                          <Icon className="h-5 w-5" />
                        </span>
                      </div>
                      <div className="lg:col-span-9">
                        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-crimson">
                          Seminario {s.id} · {s.category}
                        </span>
                        <h3 className="mt-3 max-w-3xl font-display text-xl font-extrabold leading-tight tracking-tight sm:text-2xl lg:text-3xl">
                          {s.title}
                        </h3>
                        <div className="mt-7 grid gap-8 md:grid-cols-2">
                          <div>
                            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-navy/40">
                              Objetivo de aprendizaje
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-navy/70">{s.objective}</p>
                          </div>
                          <div className="border-l-2 border-crimson pl-6">
                            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-crimson">
                              Beneficio
                            </p>
                            <p className="mt-3 font-serif text-lg italic leading-relaxed text-navy/85">{s.benefit}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ============ BONOS ============ */}
      <section data-testid="bonuses-section" className="noise relative overflow-hidden bg-navy-deep py-16 sm:py-24 lg:py-32">
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <FadeUp>
                <Overline className="text-gold">Además incluye</Overline>
              </FadeUp>
              <MaskedLines
                inView
                className="mt-6 font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl"
                lines={[<>Bonos, workbooks</>, <>y <span className="text-crimson">mentoría en vivo</span></>]}
              />
              <div className="mt-10 space-y-0">
                {BONUSES.map((b, i) => (
                  <FadeUp key={b.title} delay={0.1 + i * 0.1}>
                    <div className="group flex items-start gap-5 border-t border-white/10 py-6 last:border-b hover:bg-white/[0.03] transition-colors duration-500">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-crimson/15 text-crimson transition-colors duration-500 group-hover:bg-crimson group-hover:text-white">
                        <b.icon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-display text-lg font-bold sm:text-xl">{b.title}</h3>
                        <p className="mt-1.5 max-w-md text-sm leading-relaxed text-white/55">{b.desc}</p>
                      </div>
                    </div>
                  </FadeUp>
                ))}
              </div>
            </div>
            <FadeUp delay={0.2} className="lg:col-span-6">
              <div className="relative mx-auto max-w-md">
                <img src="/images/bonos-tablet.jpg" alt="Tablet con los bonos del programa" className="w-full mix-blend-screen" data-testid="bonuses-image" />
                <img
                  src="/images/ipad-sesion.jpg"
                  alt="Sesión en vivo del programa"
                  className="absolute -bottom-10 -left-10 w-40 mix-blend-screen sm:-left-16 sm:w-52"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
